import { Spinner, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import type React from 'react'
import { useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import DefaultModal from '../../../../../../components/DefaultModal'
import FormInput from '../../../../../../components/FormInput'
import Switch from '../../../../../../components/Switch'
import Toast from '../../../../../../components/Toast'
import { useUpdateAffiliatePartnerMutation } from '../../../../../../redux/services/coreApi'
import type { AffiliatePartnerDto } from '../../../../../../types/coreApi-types'

interface UpdateAffiliatePartnerModalProps {
  isOpen: boolean
  onClose: () => void
  affiliatePartner?: AffiliatePartnerDto
}

type FormData = {
  name: string
  contactEmail: string
  commissionRate: number
  referralCodes?: string
  partnershipStartDate: string
}

const UpdateAffiliatePartnerModal: React.FC<
  UpdateAffiliatePartnerModalProps
> = ({ isOpen, onClose, affiliatePartner }) => {
  const schema: yup.SchemaOf<FormData> = yup.object({
    name: yup.string().required('This field is required.'),
    contactEmail: yup
      .string()
      .email('Please enter a valid email')
      .required('This field is required.'),
    commissionRate: yup
      .number()
      .min(0, 'Commission rate must be at least 0%')
      .max(100, 'Commission rate cannot exceed 100%')
      .required('This field is required.'),
    referralCodes: yup.string().optional(),
    partnershipStartDate: yup.string().required('This field is required.'),
  })

  const {
    register,
    handleSubmit,
    formState,
    reset: resetForm,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  })

  const toast = useToast()
  const [isActive, setIsActive] = useState(false)
  const [regenerateApiKey, setRegenerateApiKey] = useState(false)

  const [
    updateAffiliatePartner,
    { isLoading: updateAffiliatePartnerIsLoading },
  ] = useUpdateAffiliatePartnerMutation()

  const onSubmit: SubmitHandler<FormData> = async ({
    name,
    contactEmail,
    commissionRate,
    referralCodes = '',
    partnershipStartDate,
  }) => {
    if (!affiliatePartner?.id) return

    try {
      await updateAffiliatePartner({
        id: affiliatePartner.id,
        name,
        contactEmail,
        commissionRate: commissionRate / 100, // Convert percentage to decimal
        isActive,
        referralCodes: referralCodes
          .split(',')
          .map(code => code.trim())
          .filter(code => code.length > 0),
        partnershipStartDate,
        regenerateApiKey,
      }).unwrap()

      toast({
        position: 'top-right',
        render: () => (
          <Toast type="success">Affiliate partner updated successfully!</Toast>
        ),
      })
      onClose()
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            Failed to update affiliate partner. Please try again.
          </Toast>
        ),
      })
    }
  }

  useEffect(() => {
    if (isOpen && affiliatePartner) {
      setValue('name', affiliatePartner.name)
      setValue('contactEmail', affiliatePartner.contactEmail)
      setValue('commissionRate', affiliatePartner.commissionRate * 100) // Convert to percentage
      setValue(
        'referralCodes',
        affiliatePartner.referralCodes?.join(', ') || ''
      )
      setValue(
        'partnershipStartDate',
        affiliatePartner.partnershipStartDate
          ? new Date(affiliatePartner.partnershipStartDate)
              .toISOString()
              .split('T')[0]
          : ''
      )
      setIsActive(affiliatePartner.isActive)
      setRegenerateApiKey(false)
    }
  }, [isOpen, affiliatePartner, setValue])

  useEffect(() => {
    if (!isOpen) {
      resetForm()
      setIsActive(false)
      setRegenerateApiKey(false)
    }
  }, [isOpen, resetForm])

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={false}
      title="Update Affiliate Partner"
      whiteBG
      maxW="600px"
    >
      <div className="p-5 pt-0 space-y-5">
        <FormInput
          inputMode="text"
          registeredForm={register('name')}
          error={formState.errors?.name}
          label="Partner Name"
          placeholder="Enter partner name"
        />

        <FormInput
          inputMode="email"
          registeredForm={register('contactEmail')}
          error={formState.errors?.contactEmail}
          label="Contact Email"
          placeholder="Enter contact email"
        />

        <FormInput
          inputMode="numeric"
          registeredForm={register('commissionRate')}
          error={formState.errors?.commissionRate}
          label="Commission Rate (%)"
          placeholder="Enter commission rate (e.g., 5 for 5%)"
        />

        <FormInput
          inputMode="text"
          registeredForm={register('referralCodes')}
          error={formState.errors?.referralCodes}
          label="Referral Codes (comma-separated)"
          placeholder="Enter referral codes separated by commas"
        />

        <FormInput
          inputMode="text"
          type="date"
          registeredForm={register('partnershipStartDate')}
          error={formState.errors?.partnershipStartDate}
          label="Partnership Start Date"
          placeholder="Select partnership start date"
        />

        <div className="space-y-4">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setIsActive(!isActive)}
          >
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Active Status
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Enable or disable this affiliate partner
              </p>
            </div>
            <Switch isOn={isActive} />
          </div>

          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setRegenerateApiKey(!regenerateApiKey)}
          >
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Regenerate API Key
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Generate a new API key for this partner
              </p>
            </div>
            <Switch isOn={regenerateApiKey} />
          </div>
        </div>

        {regenerateApiKey && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Warning:</strong> Regenerating the API key will invalidate
              the current key. Make sure to update the partner with the new key
              after saving.
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <button
            className="button-secondary flex-1"
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="button flex-1"
            disabled={!formState.isValid || updateAffiliatePartnerIsLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {updateAffiliatePartnerIsLoading ? (
              <Spinner size="sm" />
            ) : (
              'Update Partner'
            )}
          </button>
        </div>
      </div>
    </DefaultModal>
  )
}

export default UpdateAffiliatePartnerModal
